import clsx from "clsx";
import React from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router";
import { useDrawerContext } from "src/ui/navigation/components/Drawer/Drawer";

import { ListItemButton, ListItemButtonProps, Typography, TypographyProps } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import ListItemIcon from "@mui/material/ListItemIcon";

import { useStyles } from "./NavigationLinks.style";

interface BaseLinkProps extends Omit<ListItemButtonProps<"div">, "title"> {
    title: string;
    condition?: boolean;
    textProps?: TypographyProps;
    startIcon?: string;
    endIcon?: string;
    notCollapsible?: boolean;
    href?: string;
    to?: string;
    target?: string;
    rel?: string;
}
const BaseLink = (props: BaseLinkProps) => {
    const { condition = true, textProps = {}, startIcon, endIcon, className, ...rest } = props;
    const { classes } = useStyles();
    const drawer = useDrawerContext();

    if (!condition) {
        return null;
    }

    if (!drawer.open && !props.notCollapsible) {
        return (
            <div>
                <div className={classes.listItemBox}>
                    <ListItemButton
                        {...rest}
                        aria-label={props.title}
                        aria-current={props["aria-current"] ?? (props.selected ? "page" : undefined)}
                        className={clsx(classes.listItem, className)}
                    >
                        <ListItemIcon className={classes.startIcon}>
                            <i className={startIcon} />
                        </ListItemIcon>
                    </ListItemButton>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.listItemBox}>
            <ListItemButton
                {...rest}
                aria-current={props["aria-current"] ?? (props.selected ? "page" : undefined)}
                className={clsx(classes.listItem, className)}
            >
                {startIcon ? (
                    <ListItemIcon className={classes.startIcon}>
                        <i className={startIcon} />
                    </ListItemIcon>
                ) : null}
                <Typography className={classes.text} {...textProps}>
                    {props.title}
                </Typography>
                {endIcon ? (
                    <ListItemIcon className={classes.endIcon}>
                        <i className={endIcon} />
                    </ListItemIcon>
                ) : null}
            </ListItemButton>
        </div>
    );
};

interface LegacyLinkProps extends BaseLinkProps {
    href: string;
}
export const LegacyLink = (props: LegacyLinkProps) => {
    const location = useLocation();
    return <BaseLink {...props} selected={location.pathname === props.href} />;
};

interface RouterLinkProps extends BaseLinkProps {
    to: string;
    resetOnReselect?: boolean;
}
export const RouterLink = (props: RouterLinkProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { resetOnReselect, to, ...baseLinkProps } = props;

    const isCurrent = location.pathname === to;

    const handleClick = (event: React.MouseEvent) => {
        if (resetOnReselect && isCurrent) {
            event.preventDefault();
            void navigate(to, { replace: true, state: { reset: Date.now() } });
        }
    };

    return <BaseLink {...baseLinkProps} LinkComponent={Link} to={to} onClick={handleClick} selected={isCurrent} />;
};

interface ExternalLinkProps extends BaseLinkProps {
    url: string;
}
export const ExternalLink = ({ url, ...props }: ExternalLinkProps) => {
    return <BaseLink {...props} href={url} target="_blank" rel="noopener noreferrer" />;
};

type OpenSubmenuContextType = {
    /**
     * The submenu the user opened by hand on the current page, `null` if they closed all of them, or `undefined`
     * if they have not touched the submenus since arriving on this page.
     */
    chosenId: string | null | undefined;
    choose: (id: string | null) => void;
};
const OpenSubmenuContext = React.createContext<OpenSubmenuContextType | null>(null);

/**
 * Lets only one submenu inside it be open at a time, so opening one never pushes the rest of the sidebar out of view.
 * Until the user opens or closes one by hand, the submenu holding the current page is the open one.
 */
export const SingleOpenSubmenu = ({ children }: React.PropsWithChildren) => {
    const drawer = useDrawerContext();
    const { pathname } = useLocation();
    const [choice, setChoice] = React.useState<{ id: string | null; pathname: string } | null>(null);
    const [wasDrawerOpen, setWasDrawerOpen] = React.useState(drawer.open);

    // Collapsing the sidebar forgets the choice, so expanding it again shows the current page's submenu.
    if (drawer.open !== wasDrawerOpen) {
        setWasDrawerOpen(drawer.open);
        if (!drawer.open) {
            setChoice(null);
        }
    }

    const value = React.useMemo(
        () => ({
            chosenId: choice?.pathname === pathname ? choice.id : undefined,
            choose: (id: string | null) => setChoice({ id, pathname }),
        }),
        [choice, pathname],
    );
    return <OpenSubmenuContext.Provider value={value}>{children}</OpenSubmenuContext.Provider>;
};

interface CollapsibleLinkProps extends React.PropsWithChildren<BaseLinkProps> {
    /**
     * Path under which this group's pages live. While one of them is shown, the group opens on every navigation
     * and its header stays highlighted whenever the group is closed.
     */
    activePath?: string;
}
export const CollapsibleLink = ({ condition = true, activePath, ...props }: CollapsibleLinkProps) => {
    const id = React.useId();
    const submenus = React.useContext(OpenSubmenuContext);
    const drawer = useDrawerContext();
    const { pathname } = useLocation();
    if (!submenus) {
        throw new Error("CollapsibleLink must be used within SingleOpenSubmenu");
    }

    const isActive = activePath !== undefined && matchPath(`${activePath}/*`, pathname) !== null;
    const open = drawer.open && (submenus.chosenId !== undefined ? submenus.chosenId === id : isActive);
    const isHighlighted = isActive && !open;

    if (!condition) {
        return null;
    }

    return (
        <div>
            <BaseLink
                {...props}
                selected={isHighlighted}
                aria-current={isHighlighted ? "true" : undefined}
                aria-expanded={open}
                endIcon={open ? "fa-solid fa-angle-down" : "fa-solid fa-angle-right"}
                onClick={() => {
                    if (!drawer.open) {
                        drawer.setOpen(true);
                    }
                    submenus.choose(open ? null : id);
                }}
            />
            <Collapse in={open} timeout="auto" unmountOnExit style={{ paddingLeft: 10 }}>
                {props.children}
            </Collapse>
        </div>
    );
};
